//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import * as React from 'react'
import { ReactElement } from 'react'
import { FormControl, InputLabel, MenuItem, Select, TextField, Typography } from '@mui/material'
import { FieldMetaData, StringTypeEnum } from '@shared/SchemaTypes'
import { EthAddr } from '@shared/SchemaTypes'
import getDataValue from './getDataValue'
import EditDate from './EditDate'
import EditTime from './EditTime'
import EditNumber from './EditNumber'
import { SchemaDescription, SchemaFieldDescription } from 'yup'

import EditBoolean from './EditBoolean'
import { logOT } from '@shared/loggingShared'

interface EditFieldProps {
  fieldSchema: SchemaFieldDescription
  initialValues?: Record<string, object> | object
  onChange?: (e: Event) => void
  register: any
  errors: any
  setValue?: any
  addr?: EthAddr
  currency?: string
}

export default function EditField(props: EditFieldProps): ReactElement {
  const { addr, fieldSchema, initialValues, errors, register, onChange, setValue, currency } = props
  const castErrors = errors as Record<string, Error>
  const schemaDescription = fieldSchema as SchemaDescription
  const fieldMetaData = schemaDescription.meta as FieldMetaData

  const propName: string = fieldMetaData.propName

  const defaultValue = getDataValue(initialValues as Record<string, object | string>, propName)
  logOT.debug('Default Value ', propName, defaultValue)

  const label = schemaDescription.label ?? ''

  if (fieldSchema.type === 'boolean') {
    return (
      <EditBoolean
        fieldSchema={fieldSchema}
        errors={errors}
        register={register}
        addr={addr}
        setValue={setValue}
        initialValues={initialValues}
      />
    )
  }
  if (schemaDescription.type === 'string') {
    if (fieldMetaData.stringType === StringTypeEnum.ETH_DATE) {
      return (
        <EditDate
          propName={propName}
          label={label}
          errors={errors}
          onChange={onChange}
          setValue={setValue}
          register={register}
          defaultValue={defaultValue}
        />
      )
    }
    if (fieldMetaData.stringType === StringTypeEnum.TIME_INTERVAL_HOURS) {
      return (
        <EditTime
          setValue={setValue}
          propName={propName}
          label={label}
          onChange={onChange}
          errors={errors}
          register={register}
          defaultValue={defaultValue}
        />
      )
    }

    return (
      <React.Fragment>
        <TextField
          inputProps={{
            'data-testid': propName,
          }}
          InputLabelProps={{
            shrink: true,
          }}
          defaultValue={defaultValue}
          required
          id={propName}
          label={label}
          fullWidth
          margin="dense"
          {...register(propName)}
          error={castErrors[propName] != null}
        />
        <Typography variant="inherit" color="textSecondary">
          {errors[propName]?.message}
        </Typography>
      </React.Fragment>
    )
  }
  if (fieldSchema.type === 'number') {
    return (
      <EditNumber
        fieldSchema={fieldSchema}
        errors={errors}
        register={register}
        addr={addr}
        currency={currency}
        initialValues={initialValues}
      />
    )
  }
  if (fieldSchema.type === 'mixed') {
    // logOT.debug('Mixed ', fieldSchema.meta()?.choices)
    const choices: string[] = fieldMetaData.choices ?? []
    // logOT.debug('propName', propName, 'defaultValue-', defaultValue, 'initialValues', initialValues)

    return (
      <React.Fragment>
        <FormControl fullWidth>
          <InputLabel id={propName + 'label'}>{label}</InputLabel>
          <Select
            labelId={propName + 'label'}
            id={propName}
            label={label}
            defaultValue={defaultValue}
            required
            fullWidth
            margin="dense"
            {...register(propName)}
            error={castErrors[propName] != null}
          >
            {choices.map((choice: string): ReactElement => {
              if (!choice) {
                return (
                  <MenuItem key={'choice'} value={'s'}>
                    {choice}
                  </MenuItem>
                )
              } else {
                return (
                  <MenuItem key={choice} value={choice}>
                    {choice}
                  </MenuItem>
                )
              }
            })}
          </Select>
        </FormControl>

        <Typography variant="inherit" color="textSecondary">
          {errors[propName]?.message}
        </Typography>
      </React.Fragment>
    )
  }
  if (fieldSchema.type === 'date') {
    return (
      <EditDate
        setValue={setValue}
        propName={propName}
        label={label}
        onChange={onChange}
        errors={errors}
        register={register}
        defaultValue={defaultValue}
      />
    )
  }

  if (fieldSchema.type === 'time') {
    return (
      <EditTime
        setValue={setValue}
        propName={propName}
        label={label}
        onChange={onChange}
        errors={errors}
        register={register}
        defaultValue={defaultValue}
      />
    )
  }

  console.error('UNDEFINED TYPE FOR EDIT ', fieldSchema.type, fieldSchema)
  return (
    <React.Fragment>
      <Typography variant="inherit" color="textSecondary">
        Undefined Type {fieldSchema.type}
      </Typography>
    </React.Fragment>
  )
}
